// libs
import { useContext, useMemo } from "react";

// internal deps
import { PaymentGroupDataDef } from "@swiggy-private/payment-ui";

// interfaces
import { FRICTION_LAYOUT_ID } from "@payment-client/interfaces/PaymentApi";

// helpers
import {
    getFrictionLayoutId,
    shouldSplitRenderingGroups,
    splitRenderingPaymentGroups,
} from "@payment-client/helpers/PaymentHelper";
// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

export interface FrictionLayoutState {
    showFrictionLayout: boolean;
    frictionLayoutId: FRICTION_LAYOUT_ID;
    frictionGroups: PaymentGroupDataDef[];
    topGroups: PaymentGroupDataDef[];
    bottomGroups: PaymentGroupDataDef[];
}

const useFrictionLayout = (
    paymentGroups: PaymentGroupDataDef[],
    // this value will be false if all methods are applicable for coupon
    showCouponSpecificMethods: boolean,
): FrictionLayoutState => {
    const { paymentResponse } = useContext(ShowPaymentContext);
    const frictionLayoutId: FRICTION_LAYOUT_ID = getFrictionLayoutId(paymentResponse);

    // split groups only when friction layout is enabled and
    // showing all groups (not in specific coupon alliance group view)
    const [topGroups, frictionGroups, bottomGroups] = useMemo(
        () =>
            shouldSplitRenderingGroups(showCouponSpecificMethods, frictionLayoutId)
                ? splitRenderingPaymentGroups(paymentGroups)
                : [paymentGroups, [], []],
        [showCouponSpecificMethods, frictionLayoutId, paymentGroups],
    );
    const showFrictionLayout = frictionGroups.length > 0;

    return { showFrictionLayout, topGroups, frictionGroups, bottomGroups, frictionLayoutId };
};

export default useFrictionLayout;
